import Close from "@mui/icons-material/Close";
import { Box, Button, Chip, Stack, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { MouseEvent, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import {
  HiddenDisplayToken,
  matchesHiddenDisplayToken,
  mergeHiddenDisplayTokens,
} from "utils/omnisearchUtils";

import { useSearchPills } from "../../providers/searchPills";
import { FilterPopover } from "./FilterPopover";
import { pillLabel, removePairFromOmnisearch, splitOmnisearch } from "./omnisearchPills";
import { EMPTY_KEY_ALIASES, findFilterKeyForPair } from "./pillFilterKeys";
import { isFacilityFilterKeyType, isTenantFilterKeyType, usePillValueNames } from "./usePillNames";

export type OmnisearchPillRowProps = {
  /** The current omnisearch string — the source of truth for the pills. */
  omnisearch: string;
  /** Called with the rewritten omnisearch string when a pill is removed. */
  onChange: (next: string) => void;
  /** Applies the result of editing a single filter. */
  onApply: (next: string) => void;
  columnFilterKeys?: FilterKey[];
  keyAliases?: Record<string, string>;
  preservedFilterKeys?: string[];
  tenantId?: string;
  /**
   * Control-owned tokens that get no pill: a dedicated toolbar control (e.g. a
   * checkbox) already shows their state, and a pill would indicate the same
   * filter twice. They stay in the omnisearch string — removing another pill
   * rewrites around them. See {@link HiddenDisplayToken}.
   */
  hiddenDisplayTokens?: HiddenDisplayToken[];
};

const EMPTY_HIDDEN_DISPLAY_TOKENS: HiddenDisplayToken[] = [];

/**
 * The applied-filter pill row.
 *
 * Renders on its own full-width line below the grid toolbar, so the toolbar
 * itself keeps the layout it has without pills. Renders nothing at all unless
 * the preview is on and something is actually filtered, so a grid looks exactly
 * as it does today until the user filters.
 */
export function OmnisearchPillRow(props: OmnisearchPillRowProps) {
  const { enabled } = useSearchPills();
  const filterKeys = useMemo(() => props.columnFilterKeys ?? [], [props.columnFilterKeys]);
  const keyAliases = props.keyAliases ?? EMPTY_KEY_ALIASES;

  const hiddenDisplayTokens = props.hiddenDisplayTokens ?? EMPTY_HIDDEN_DISPLAY_TOKENS;
  const pairs = useMemo(
    () =>
      splitOmnisearch(enabled ? props.omnisearch : "").pairs.filter(
        (pair) => !matchesHiddenDisplayToken(pair.key, pair.values, hiddenDisplayTokens)
      ),
    [enabled, props.omnisearch, hiddenDisplayTokens]
  );

  // Facility and tenant filters carry ids rather than names.
  const { facilityIds, tenantIds } = useMemo(() => {
    const facilityIds: string[] = [];
    const tenantIds: string[] = [];
    pairs.forEach((pair) => {
      const filterKeyType = findFilterKeyForPair(pair.key, filterKeys, keyAliases)?.filterKeyType;
      if (isFacilityFilterKeyType(filterKeyType)) {
        facilityIds.push(...pair.values);
      } else if (isTenantFilterKeyType(filterKeyType)) {
        tenantIds.push(...pair.values);
      }
    });
    return { facilityIds, tenantIds };
  }, [pairs, filterKeys, keyAliases]);

  if (!enabled || pairs.length === 0) {
    return null;
  }

  // Resolving names needs Apollo, and merely calling useQuery requires a client
  // and registers the query — enough to break grids rendered without a provider
  // and to confuse tests that assert on "the last query". So only reach for it
  // when there is actually an id to look up.
  if (facilityIds.length === 0 && tenantIds.length === 0) {
    return <PillRow {...props} valueNames={EMPTY_VALUE_NAMES} />;
  }
  return <PillRowWithValueNames {...props} facilityIds={facilityIds} tenantIds={tenantIds} />;
}

const EMPTY_VALUE_NAMES: ReadonlyMap<string, string> = new Map();

function PillRowWithValueNames({
  facilityIds,
  tenantIds,
  ...props
}: OmnisearchPillRowProps & { facilityIds: string[]; tenantIds: string[] }) {
  const valueNames = usePillValueNames(facilityIds, tenantIds);
  return <PillRow {...props} valueNames={valueNames} />;
}

function PillRow({
  omnisearch,
  onChange,
  onApply,
  columnFilterKeys,
  keyAliases = EMPTY_KEY_ALIASES,
  preservedFilterKeys,
  tenantId,
  valueNames,
  hiddenDisplayTokens = EMPTY_HIDDEN_DISPLAY_TOKENS,
}: OmnisearchPillRowProps & { valueNames: ReadonlyMap<string, string> }) {
  // The pill an editor is open for, with the chip it is anchored to.
  const [editingPair, setEditingPair] = useState<{ key: string; anchor: HTMLElement } | null>(null);

  const filterKeys = useMemo(() => columnFilterKeys ?? [], [columnFilterKeys]);
  const pairs = useMemo(
    () =>
      splitOmnisearch(omnisearch).pairs.filter(
        (pair) => !matchesHiddenDisplayToken(pair.key, pair.values, hiddenDisplayTokens)
      ),
    [omnisearch, hiddenDisplayTokens]
  );

  const handleCloseEditor = useCallback(() => setEditingPair(null), []);

  // Scope the popover to the single field being edited. Its apply path
  // preserves every key it does not manage, so editing one pill leaves the
  // other filters and the free text untouched.
  const editingColumnFilterKeys = useMemo(() => {
    if (!editingPair) {
      return [];
    }
    const filterKey = findFilterKeyForPair(editingPair.key, filterKeys, keyAliases);
    return filterKey ? [filterKey] : [];
  }, [filterKeys, keyAliases, editingPair]);

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      // Full width so the toolbar's wrapping row drops this onto its own line.
      sx={{ width: "100%", flexWrap: "wrap", alignItems: "center" }}
    >
      {pairs.map((pair) => {
        const filterKey = findFilterKeyForPair(pair.key, filterKeys, keyAliases);
        return (
          <FilterPill
            key={pair.key}
            label={pillLabel(pair, filterKey, valueNames)}
            isEditable={Boolean(filterKey)}
            onEdit={(anchor) => setEditingPair({ key: pair.key, anchor })}
            onRemove={() => onChange(removePairFromOmnisearch(omnisearch, pair.key))}
          />
        );
      })}

      {/* Clears the pills and the free-text search with them — which is what
          "clear" reads as when both sit in the same box. Control-owned hidden
          tokens survive, exactly as they do for the text input's clear X; their
          checkboxes are how those are turned off. */}
      <Button
        size="small"
        // Matched to the pill height. Left at its own 30px the button's box —
        // and the hover fill inside it — stands taller than the pills beside
        // it, which reads as the row being out of alignment.
        sx={{ color: "action.active", minHeight: PILL_HEIGHT, py: 0 }}
        onClick={() =>
          onChange(mergeHiddenDisplayTokens("", omnisearch, hiddenDisplayTokens) ?? omnisearch)
        }
      >
        Clear filters
      </Button>

      {editingPair && editingColumnFilterKeys.length > 0 && (
        <FilterPopover
          isOpen
          filterButtonAnchor={editingPair.anchor}
          handleCloseFilter={handleCloseEditor}
          columnFilterKeys={editingColumnFilterKeys}
          onApply={onApply}
          omnisearch={omnisearch}
          savedFilters={EMPTY_SAVED_FILTERS}
          keyAliases={keyAliases}
          preservedFilterKeys={preservedFilterKeys}
          tenantId={tenantId}
          compact
        />
      )}
    </Stack>
  );
}

/**
 * How wide a pill may get before its label is cut short.
 *
 * A filter over several values ("Encamp Status: Not Reporting, Filing
 * Complete, Awaiting Verification") would otherwise stretch the pill past the
 * search box and squeeze everything beside it. The `100%` keeps a pill inside a
 * narrow container as well, where even one value can be too wide.
 */
const PILL_MAX_WIDTH = "min(100%, 20rem)";

/** MUI's own height for a `small` Chip, shared with the row's Clear button. */
const PILL_HEIGHT = 24;

function FilterPill({
  label,
  isEditable,
  onEdit,
  onRemove,
}: {
  label: string;
  isEditable: boolean;
  onEdit: (anchor: HTMLElement) => void;
  onRemove: () => void;
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isClipped, setIsClipped] = useState(false);

  return (
    <Tooltip
      title={pillTooltip(label, isClipped, isEditable)}
      // Measured as the tooltip opens rather than on hover, so a keyboard user
      // tabbing to the pill gets the same answer a mouse user does.
      onOpen={() => {
        const el = textRef.current;
        if (el) {
          setIsClipped(el.scrollWidth > el.clientWidth);
        }
      }}
    >
      <Chip
        size="small"
        label={
          <Box
            component="span"
            ref={textRef}
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Box>
        }
        // Light-filled neutral chip per the design system: the info role at
        // 8% alpha, never MUI's solid dark fill.
        sx={{
          maxWidth: PILL_MAX_WIDTH,
          color: "info.main",
          bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
          // The label is what gives; the remove button keeps its size however
          // long the text is.
          "& .MuiChip-label": { minWidth: 0 },
          "& .MuiChip-deleteIcon": { flexShrink: 0 },
          // An editable pill deepens on hover. That plus the pointer cursor is
          // the whole affordance for "click me to edit" — an icon inside the
          // pill reads as a second control next to the remove button.
          ...(isEditable && {
            "&:hover": { bgcolor: (theme) => alpha(theme.palette.info.main, 0.16) },
          }),
        }}
        onClick={
          isEditable ? (event: MouseEvent<HTMLElement>) => onEdit(event.currentTarget) : undefined
        }
        onDelete={onRemove}
        deleteIcon={<Close aria-label={`Remove ${label}`} fontSize="small" />}
      />
    </Tooltip>
  );
}

/** Spells out the affordance the pill's hover state only hints at. */
const EDIT_HINT = "Modify filter";

/**
 * What a pill says on hover.
 *
 * The full label earns a tooltip only when it is actually cut short, otherwise
 * it would just repeat what the pill already reads. The hint always shows on an
 * editable pill, and sits on its own italicized line beneath a clipped label so
 * it reads as guidance rather than as part of the filter.
 */
function pillTooltip(label: string, isClipped: boolean, isEditable: boolean): ReactNode {
  if (!isClipped) {
    return isEditable ? EDIT_HINT : "";
  }
  if (!isEditable) {
    return label;
  }
  return (
    <>
      {label}
      <Box component="em" sx={{ display: "block" }}>
        {EDIT_HINT}
      </Box>
    </>
  );
}

// The compact editor has no saved-filter selector — presets are chosen from the
// full filter popover.
const EMPTY_SAVED_FILTERS: never[] = [];
